import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { setPostAuthRedirect } from '../services/api';
import { toast } from '../utils/toastBus';

export default function VideoCreator() {
  const navigate = useNavigate();
  const [projects, setProjects] = React.useState([]);
  const [pid, setPid] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [assets, setAssets] = React.useState({ images: [], audio: [], videos: [] });
  const [specificContext, setSpecificContext] = React.useState('');
  const [prompts, setPrompts] = React.useState([]); // [{ text, estimatedScore }]
  const [chosenPrompt, setChosenPrompt] = React.useState('');
  const [audioAssetId, setAudioAssetId] = React.useState('');
  const [imageAssetId, setImageAssetId] = React.useState('');
  const [promptsLoading, setPromptsLoading] = React.useState(false);
  const [credits, setCredits] = React.useState(null);
  const [creditsLoading, setCreditsLoading] = React.useState(true);
  const copyText = async (text) => { try { await navigator.clipboard.writeText(String(text || '')); toast({ type: 'success', message: 'Link copied' }); } catch {} };
  const deleteAsset = async (asset) => {
    try {
      const assetId = asset?.id || asset?.assetId || asset?._id;
      if (!assetId) return;
      const ok = typeof window !== 'undefined' ? window.confirm('Delete this asset? This cannot be undone.') : true;
      if (!ok) return;
      setBusy(true);
      const r = await api.composerAssetDelete(assetId);
      if (!r?.error) { try { toast({ type: 'success', message: 'Deleted' }); } catch {} ; loadAssets(pid); }
      else { try { toast({ type: 'error', message: 'Delete failed' }); } catch {} }
    } finally { setBusy(false); }
  };

  async function load() {
    const res = await api.composerListProjects();
    const items = Array.isArray(res?.items) ? res.items : [];
    setProjects(items);
    if (!pid && items.length) setPid(items[0].id);
  }

  React.useEffect(() => { load(); }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.getMyCredits();
        if (r && typeof r.balance === 'number') setCredits(Number(r.balance));
        else setCredits(null);
      } catch {
        setCredits(null);
      } finally {
        setCreditsLoading(false);
      }
    })();
  }, []);

  async function loadAssets(projectId) {
    if (!projectId) return;
    try {
      const r = await fetch(`${api.BASE}/api/composer/projects/${encodeURIComponent(String(projectId))}/assets?limit=50`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      setAssets({
        images: Array.isArray(j?.items?.images) ? j.items.images : [],
        audio: Array.isArray(j?.items?.audio) ? j.items.audio : [],
        videos: Array.isArray(j?.items?.videos) ? j.items.videos : [],
      });
      // Set defaults for selectors if empty
      const firstAudio = (Array.isArray(j?.items?.audio) ? j.items.audio : [])[0];
      const firstImage = (Array.isArray(j?.items?.images) ? j.items.images : [])[0];
      if (!audioAssetId && firstAudio) setAudioAssetId(String(firstAudio.id || firstAudio.assetId || firstAudio._id || ''));
      if (!imageAssetId && firstImage) setImageAssetId(String(firstImage.id || firstImage.assetId || firstImage._id || ''));
    } catch {}
  }

  React.useEffect(() => { if (pid) loadAssets(pid); }, [pid]);

  async function runStatic() {
    if (!pid) return;
    if (!ensureCreditsAvailable()) return;
    setBusy(true);
    try {
      const r = await api.composerCreateAudioImage(pid);
      setResult(r?.item || r || null);
    } finally { setBusy(false); }
  }

  async function runDynamic() {
    if (!pid) return;
    if (!ensureCreditsAvailable()) return;
    if (!audioAssetId || !imageAssetId) {
      try { toast({ type: 'warning', message: 'Select an audio and an image asset first' }); } catch {}
      return;
    }
    setBusy(true);
    try {
      const payload = {
        audioAssetId,
        imageAssetId,
        chosenPrompt: chosenPrompt ? String(chosenPrompt) : undefined,
        specificContext: specificContext ? String(specificContext) : undefined,
        aspectRatio: aspect,
        durationSec: duration,
        tier,
      };
      const r = await api.composerCreateVideo(pid, payload);
      setResult(r?.item || r || null);
    } finally { setBusy(false); }
  }

  async function finalize() {
    if (!pid) return;
    if (!ensureCreditsAvailable()) return;
    setBusy(true);
    try {
      const r = await api.composerFinalize(pid);
      setResult(r?.item || r || null);
    } finally { setBusy(false); }
  }

  const requiresPurchase = !creditsLoading && (credits === null || Number(credits) <= 0);

  const handleBuyTokens = React.useCallback(async (packId = '100') => {
    try {
      const res = await api.startCreditsPurchase(packId);
      if (res?.requiresAuth) {
        setPostAuthRedirect('/profile');
        navigate('/login?redirectTo=/profile');
        return;
      }
      if (res?.error) {
        toast({ type: 'error', message: res.error });
      }
    } catch (err) {
      toast({ type: 'error', message: err?.message || 'Checkout unavailable' });
    }
  }, [navigate]);

  const ensureCreditsAvailable = () => {
    if (creditsLoading) return false;
    if (credits === null || Number(credits) <= 0) {
      toast({ type: 'warning', message: 'Purchase credits to generate new videos.' });
      handleBuyTokens();
      return false;
    }
    return true;
  };

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ color: '#e5e7eb', margin: '8px 0 16px 0', fontSize: 22, fontWeight: 800 }}>Video Creator</h1>
      {requiresPurchase && (
        <div style={{ border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#fbbf24', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Tokens required</div>
          <div style={{ fontSize: 14, color: '#fde68a' }}>
            Purchase credits before generating videos. <button onClick={() => handleBuyTokens()} style={{ background: 'transparent', border: 0, color: '#fbbf24', textDecoration: 'underline', cursor: 'pointer' }}>Buy tokens</button>
          </div>
        </div>
      )}
      <div style={{ display: 'grid', gap: 8, maxWidth: 720 }}>
        <label style={{ color: '#9ca3af', fontSize: 12 }}>Project</label>
        <select value={pid} onChange={(e) => setPid(e.target.value)} style={sel()}>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
          <div style={{ color: '#e5e7eb', fontWeight: 700 }}>Specific Context (optional)</div>
          <textarea
            value={specificContext}
            onChange={(e)=>setSpecificContext(e.target.value)}
            rows={3}
            placeholder="Add extra context just for this video (e.g., call‑to‑action, offer, scene)"
            style={{ background: '#0f1115', color: '#e5e7eb', border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              disabled={!pid || busy || promptsLoading || requiresPurchase}
              onClick={async()=>{
                try {
                  if (!ensureCreditsAvailable()) return;
                  setBusy(true);
                  setPromptsLoading(true);
                  const r = await api.composerGeneratePrompts(pid, { specificContext, type: 'video' });
                  const list = (r && typeof r === 'object' && Array.isArray(r.items)) ? r.items : (Array.isArray(r) ? r : []);
                  const cleaned = list.filter(it => it && typeof it.text === 'string' && it.text.trim().length);
                  setPrompts(cleaned);
                  if (cleaned[0]?.text) setChosenPrompt(String(cleaned[0].text));
                } finally { setPromptsLoading(false); setBusy(false); }
              }}
              style={btn('#374151')}
            >{promptsLoading ? 'Generating…' : 'Generate Prompts'}</button>
          </div>
          {!!prompts.length && (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Pick a prompt (scores are heuristic)</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {prompts.map((p, idx) => (
                  <label key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <input
                      type="radio"
                      name="chosenPrompt"
                      checked={String(chosenPrompt||'') === String(p?.text||'')}
                      onChange={()=> setChosenPrompt(String(p?.text || ''))}
                      style={{ marginTop: 4 }}
                    />
                    <div style={{ display: 'grid', gap: 2 }}>
                      <div style={{ color: '#e5e7eb', fontSize: 13, whiteSpace: 'pre-wrap' }}>{p?.text}</div>
                      {typeof p?.estimatedScore === 'number' && (
                        <div style={{ color: '#9ca3af', fontSize: 12 }}>estimatedScore: {p.estimatedScore}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <button disabled={busy || !pid} onClick={runStatic} style={btn('#374151')}>Static Video</button>
          <button disabled={busy || !pid || !audioAssetId || !imageAssetId || requiresPurchase} onClick={runDynamic} style={btn('#ff5a1f')}>Dynamic Video</button>
          <button disabled={busy || !pid || requiresPurchase} onClick={finalize} style={btn('#0ea5e9')}>Finalize (duck)</button>
          <button onClick={load} style={btn('#111827')}>Refresh Projects</button>
          <button onClick={() => loadAssets(pid)} style={btn('#0f172a')}>Refresh Assets</button>
          {!!pid && (
            <Link to={`/projects/${encodeURIComponent(String(pid))}`} style={{ ...btn('#4b5563'), textDecoration: 'none', display: 'inline-block' }}>Open Project</Link>
          )}
        </div>
        {chosenPrompt && (
          <div style={{ marginTop: 8, border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}>
            <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Selected Prompt</div>
            <div style={{ color: '#e5e7eb', fontSize: 13, whiteSpace: 'pre-wrap' }}>{chosenPrompt}</div>
          </div>
        )}

        {!!pid && (
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            <div style={{ color: '#e5e7eb', fontWeight: 700 }}>Select Assets</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Audio</div>
                <select value={audioAssetId} onChange={(e)=>setAudioAssetId(e.target.value)} style={sel()}>
                  {assets.audio.map((a, i) => {
                    const id = a.id || a.assetId || a._id;
                    return <option key={id || i} value={String(id || '')}>{a.name || a.filename || (a.url ? a.url.split('/').slice(-1)[0] : `Audio ${i+1}`)}</option>;
                  })}
                </select>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Image</div>
                <select value={imageAssetId} onChange={(e)=>setImageAssetId(e.target.value)} style={sel()}>
                  {assets.images.map((img, i) => {
                    const id = img.id || img.assetId || img._id;
                    return <option key={id || i} value={String(id || '')}>{img.name || (img.url ? img.url.split('/').slice(-1)[0] : `Image ${i+1}`)}</option>;
                  })}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {result?.url && (
        <div style={{ marginTop: 16 }}>
          <video controls src={result.url} style={{ width: '100%', borderRadius: 8, border: '1px solid #1f2937' }} />
        </div>
      )}

      {/* Existing Assets for selected project */}
      {!!pid && (
        <div style={{ display: 'grid', gap: 16, marginTop: 16 }}>
          <div>
            <div style={{ color: '#e5e7eb', fontWeight: 700, marginBottom: 8 }}>Videos</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
              {assets.videos.map((v, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}>
                  <video controls src={v.url} style={{ width: '100%', borderRadius: 6 }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button disabled={busy || requiresPurchase} onClick={async()=>{ if (!ensureCreditsAvailable()) return; await runStatic(); }} style={btn('#1f2937')}>Regenerate (Static)</button>
                    <a href={v.url} target="_blank" rel="noreferrer" style={{ ...btn('#4b5563'), textDecoration: 'none' }}>Open</a>
                    <button disabled={busy} onClick={()=>copyText(v.url)} style={btn('#0ea5e9')}>Share</button>
                    <button disabled={busy} onClick={()=>deleteAsset(v)} style={btn('#dc2626')}>Delete</button>
                  </div>
                </div>
              ))}
              {!assets.videos.length && <div style={{ color: '#9ca3af' }}>No videos yet</div>}
            </div>
          </div>

          <div>
            <div style={{ color: '#e5e7eb', fontWeight: 700, marginBottom: 8 }}>Images</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
              {assets.images.map((img, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}>
                  <img src={img.url} alt="cover" style={{ width: '100%', borderRadius: 6 }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button disabled={busy || requiresPurchase} onClick={async()=>{ if (!ensureCreditsAvailable()) return; setBusy(true); try { await api.composerCreateImage(pid); } finally { setBusy(false); loadAssets(pid); } }} style={btn('#374151')}>Regenerate</button>
                    <a href={img.url} target="_blank" rel="noreferrer" style={{ ...btn('#1f2937'), textDecoration: 'none' }}>Open</a>
                    <button disabled={busy} onClick={()=>copyText(img.url)} style={btn('#0ea5e9')}>Share</button>
                    <button disabled={busy} onClick={()=>deleteAsset(img)} style={btn('#dc2626')}>Delete</button>
                  </div>
                </div>
              ))}
              {!assets.images.length && <div style={{ color: '#9ca3af' }}>No images yet</div>}
            </div>
          </div>

          <div>
            <div style={{ color: '#e5e7eb', fontWeight: 700, marginBottom: 8 }}>Audio</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
              {assets.audio.map((a, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #1f2937', borderRadius: 8, padding: 8 }}>
                  <audio controls src={a.url} style={{ width: '100%' }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button disabled={busy || requiresPurchase} onClick={async()=>{ if (!ensureCreditsAvailable()) return; setBusy(true); try { await api.composerCreateAudio(pid); } finally { setBusy(false); loadAssets(pid); } }} style={btn('#374151')}>Regenerate</button>
                    <a href={a.url} target="_blank" rel="noreferrer" style={{ ...btn('#0f172a'), textDecoration: 'none' }}>Open</a>
                    <button disabled={busy} onClick={()=>copyText(a.url)} style={btn('#0ea5e9')}>Share</button>
                    <button disabled={busy} onClick={()=>deleteAsset(a)} style={btn('#dc2626')}>Delete</button>
                  </div>
                </div>
              ))}
              {!assets.audio.length && <div style={{ color: '#9ca3af' }}>No audio yet</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function btn(bg){ return { background: bg, color: '#fff', border: 0, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13 }; }
function sel(){ return { background: '#0f1115', color: '#e5e7eb', border: '1px solid #1f2937', borderRadius: 8, padding: '8px 10px' }; }
